import type { SupabaseClient } from "@supabase/supabase-js"

import {
  ExtractionLimitError,
  FREE_TIER,
  isPaidPlan,
  TaskLimitError,
  WorkspaceLimitError,
} from "@/lib/usage-limits"

/** Owned workspaces for this user (source of truth when `profiles.workspace_count` is absent). */
export async function countOwnedWorkspaces(
  supabase: SupabaseClient,
  userId: string
): Promise<number> {
  const { count, error } = await supabase
    .from("workspaces")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
  if (error) throw error
  return count ?? 0
}

/**
 * Ensure the user is allowed to create another workspace (Free: max 2 owned).
 * Uses a live count from `workspaces` so it works even if `profiles.workspace_count` was never migrated.
 */
export async function assertCanCreateWorkspace(
  supabase: SupabaseClient,
  userId: string
): Promise<void> {
  const { data, error } = await supabase.from("profiles").select("plan").eq("id", userId).maybeSingle()

  if (error) throw error

  if (isPaidPlan(data?.plan as string | undefined)) {
    return
  }

  const n = await countOwnedWorkspaces(supabase, userId)
  if (n >= FREE_TIER.maxOwnedWorkspaces) {
    throw new WorkspaceLimitError("workspace_limit")
  }
}

/**
 * Optional cache sync when the column exists. Safe no-op if `workspace_count` is not on `profiles`.
 */
export async function incrementWorkspaceCount(supabase: SupabaseClient, userId: string): Promise<void> {
  try {
    const { data, error: readErr } = await supabase
      .from("profiles")
      .select("workspace_count")
      .eq("id", userId)
      .maybeSingle()
    if (readErr) return
    const next = (data?.workspace_count as number | undefined ?? 0) + 1
    const { error: up } = await supabase.from("profiles").update({ workspace_count: next }).eq("id", userId)
    if (up) return
  } catch {
    /* column may not exist */
  }
}

export async function decrementWorkspaceCount(supabase: SupabaseClient, userId: string): Promise<void> {
  try {
    const { data, error: readErr } = await supabase
      .from("profiles")
      .select("workspace_count")
      .eq("id", userId)
      .maybeSingle()
    if (readErr) return
    const cur = (data?.workspace_count as number | undefined) ?? 0
    const next = Math.max(0, cur - 1)
    const { error: up } = await supabase.from("profiles").update({ workspace_count: next }).eq("id", userId)
    if (up) return
  } catch {
    /* column may not exist */
  }
}

export async function countTasksInWorkspace(
  supabase: SupabaseClient,
  workspaceId: string
): Promise<number> {
  const { count, error } = await supabase
    .from("workspace_tasks")
    .select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId)
  if (error) throw error
  return count ?? 0
}

export async function assertCanCreateTask(
  supabase: SupabaseClient,
  userId: string,
  workspaceId: string
): Promise<void> {
  const { data, error } = await supabase.from("profiles").select("plan").eq("id", userId).maybeSingle()
  if (error) throw error
  if (isPaidPlan(data?.plan as string | undefined)) {
    return
  }
  const n = await countTasksInWorkspace(supabase, workspaceId)
  if (n >= FREE_TIER.maxTasksPerWorkspace) {
    throw new TaskLimitError("task_limit")
  }
}

export async function assertCanExtract(
  supabase: SupabaseClient,
  userId: string
): Promise<void> {
  const { data, error } = await supabase
    .from("profiles")
    .select("plan, extraction_count")
    .eq("id", userId)
    .maybeSingle()
  if (error) throw error
  if (isPaidPlan(data?.plan as string | undefined)) {
    return
  }
  const n = data?.extraction_count ?? 0
  if (n >= FREE_TIER.maxDocumentExtractions) {
    throw new ExtractionLimitError("extraction_limit")
  }
}

export async function incrementExtractionCount(supabase: SupabaseClient, userId: string): Promise<void> {
  const { data, error: readErr } = await supabase
    .from("profiles")
    .select("plan, extraction_count")
    .eq("id", userId)
    .maybeSingle()
  if (readErr) throw readErr
  if (isPaidPlan(data?.plan as string | undefined)) {
    return
  }
  const next = (data?.extraction_count ?? 0) + 1
  const { error: up } = await supabase.from("profiles").update({ extraction_count: next }).eq("id", userId)
  if (up) throw up
}
